import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Phone, Mail, ExternalLink } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const AdRotator = ({ tenantSlug }) => {
  const { t } = useLanguage();
  const [banners, setBanners] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [lastIndex, setLastIndex] = useState(-1);

  useEffect(() => {
    if (tenantSlug) {
      fetchBanners();
    }
  }, [tenantSlug]);

  useEffect(() => {
    if (banners.length <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentIndex(prev => {
        let newIndex;
        do {
          newIndex = Math.floor(Math.random() * banners.length);
        } while (newIndex === prev && banners.length > 1);
        setLastIndex(prev);
        return newIndex;
      });
    }, 15000); // Rotate every 15 seconds
    
    return () => clearInterval(interval);
  }, [banners.length]);

  const fetchBanners = async () => {
    try {
      const response = await axios.get(`${API_URL}/banners/public/${tenantSlug}`);
      setBanners(response.data);
    } catch (error) {
      console.error('Error fetching banners:', error);
    }
  };

  if (banners.length === 0) return null;

  const currentBanner = banners[currentIndex];
  const hasContacts = currentBanner.contactPhone || currentBanner.contactEmail || currentBanner.shopUrl;

  return (
    <div className="w-full py-4 px-4 sm:px-6" data-testid="ad-rotator">
      <div className="max-w-7xl mx-auto">
        <div 
          className="relative rounded-lg overflow-hidden h-28 sm:h-36 md:h-44 banner-rotate glass-card"
          style={{
            backgroundImage: `url(${currentBanner.imageUrl})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent" />
          <div className="absolute inset-0 flex items-center justify-between p-4 sm:p-6">
            {/* Left side - Title & Description */}
            <div className="text-white flex-1" style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.8), 0 0 10px rgba(0,0,0,0.5)' }}>
              <p className="text-xs uppercase tracking-wider opacity-70" style={{ textShadow: '1px 1px 3px rgba(0,0,0,0.9)' }}>{t('sponsored')}</p>
              <h3 className="font-syne font-bold text-lg sm:text-xl md:text-2xl" style={{ textShadow: '2px 2px 6px rgba(0,0,0,0.9), 0 0 15px rgba(0,0,0,0.6)' }}>{currentBanner.title}</h3>
              {currentBanner.description && (
                <p className="text-sm opacity-90 mt-1 hidden sm:block line-clamp-2" style={{ textShadow: '1px 1px 4px rgba(0,0,0,0.9)' }}>{currentBanner.description}</p>
              )}
            </div>
            
            {/* Right side - Contact Info */}
            {hasContacts && (
              <div className="hidden md:flex flex-col gap-2 text-white text-sm ml-4" style={{ textShadow: '1px 1px 3px rgba(0,0,0,0.8)' }}>
                {currentBanner.contactPhone && (
                  <a href={`tel:${currentBanner.contactPhone}`} className="flex items-center gap-2 hover:text-primary transition-colors">
                    <Phone className="w-4 h-4" style={{ filter: 'drop-shadow(1px 1px 2px rgba(0,0,0,0.8))' }} />
                    <span>{currentBanner.contactPhone}</span>
                  </a>
                )}
                {currentBanner.contactEmail && (
                  <a href={`mailto:${currentBanner.contactEmail}`} className="flex items-center gap-2 hover:text-primary transition-colors">
                    <Mail className="w-4 h-4" style={{ filter: 'drop-shadow(1px 1px 2px rgba(0,0,0,0.8))' }} />
                    <span>{currentBanner.contactEmail}</span>
                  </a>
                )}
                {currentBanner.shopUrl && (
                  <a href={currentBanner.shopUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-primary transition-colors">
                    <ExternalLink className="w-4 h-4" style={{ filter: 'drop-shadow(1px 1px 2px rgba(0,0,0,0.8))' }} />
                    <span>{t('visitSite')}</span>
                  </a>
                )}
              </div>
            )}
          </div>
          
          {/* Mobile contact bar */}
          {hasContacts && (
            <div className="absolute bottom-0 left-0 right-0 md:hidden bg-black/60 px-4 py-2 flex items-center justify-center gap-4 text-white text-xs" style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.9)' }}>
              {currentBanner.contactPhone && (
                <a href={`tel:${currentBanner.contactPhone}`} className="flex items-center gap-1">
                  <Phone className="w-3 h-3" style={{ filter: 'drop-shadow(1px 1px 2px rgba(0,0,0,0.8))' }} />
                  <span className="truncate max-w-[100px]">{currentBanner.contactPhone}</span>
                </a>
              )}
              {currentBanner.shopUrl && (
                <a href={currentBanner.shopUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-primary">
                  <ExternalLink className="w-3 h-3" style={{ filter: 'drop-shadow(1px 1px 2px rgba(0,0,0,0.8))' }} />
                  <span>{t('visitSite')}</span>
                </a>
              )}
            </div>
          )}
        </div>
        
        {/* Dots indicator */}
        {banners.length > 1 && (
          <div className="flex justify-center gap-2 mt-3">
            {banners.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentIndex ? 'bg-primary w-4' : 'bg-muted-foreground/30'
                }`}
                aria-label={`Go to banner ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdRotator;
